import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Calendar, Plus, Trash2, MapPin, Lock, X, Image as ImageIcon, ExternalLink } from "lucide-react";
import { api, apiError, mediaUrl } from "../lib/api";
import { AddressAutocomplete } from "./AddressAutocomplete";
import { Button } from "./ui/button";
import { toast } from "sonner";

const INP = "w-full rounded-xl border-2 border-cream-bg px-3 py-2.5 outline-none focus:border-urban-teal text-urban-teal text-sm";
const emptyMarket = { name: "", city: "", address: "", schedule: "", description: "", photo: "", lat: null, lng: null };

export const MarketsManager = ({ isPro }) => {
  const [markets, setMarkets] = useState([]);
  const [editing, setEditing] = useState(null); // null | "new" | slug
  const [form, setForm] = useState(emptyMarket);
  const [picked, setPicked] = useState([]); // [{slug, name}]
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/my/markets").then((r) => setMarkets(r.data)).catch(() => {});
  useEffect(() => { if (isPro) load(); }, [isPro]);

  if (!isPro) {
    return (
      <div className="bg-white rounded-2xl shadow-warm p-6 mt-6" data-testid="markets-locked">
        <h2 className="font-display font-bold text-lg text-urban-teal flex items-center gap-2 mb-1">
          <Calendar size={18} className="text-street-gold" /> Événements & marchés
          <span className="text-[10px] bg-urban-teal text-white px-2 py-0.5 rounded-full uppercase tracking-wide">Pro</span>
        </h2>
        <p className="text-sm text-urban-teal/60 mb-4">Regroupez plusieurs foodtrucks sur un même événement (marché, festival, tournée) avec une page publique dédiée.</p>
        <div className="rounded-xl border-2 border-dashed border-cream-bg p-6 flex flex-col items-center text-center gap-2">
          <Lock className="text-urban-teal/40" size={26} />
          <p className="text-sm text-urban-teal/70 font-semibold">Créez et gérez vos marchés avec l'offre Pro.</p>
          <Link to="/tarifs" className="rounded-full bg-street-gold text-white font-bold text-sm px-5 py-2 mt-1" data-testid="markets-upgrade-btn">Passer en Pro</Link>
        </div>
      </div>
    );
  }

  const startNew = () => { setForm(emptyMarket); setPicked([]); setEditing("new"); };
  const startEdit = async (m) => {
    setForm({ name: m.name, city: m.city || "", address: m.address || "", schedule: m.schedule || "", description: m.description || "", photo: m.photo || "", lat: m.lat, lng: m.lng });
    try { const { data } = await api.get(`/markets/${m.slug}`); setPicked((data.trucks || []).map((t) => ({ slug: t.slug, name: t.name }))); }
    catch { setPicked((m.truck_slugs || []).map((s) => ({ slug: s, name: s }))); }
    setEditing(m.slug);
  };
  const cancel = () => { setEditing(null); setForm(emptyMarket); setPicked([]); setResults([]); setQ(""); };

  const searchTrucks = async (val) => {
    setQ(val);
    if (val.trim().length < 2) { setResults([]); return; }
    try { const { data } = await api.get("/trucks", { params: { search: val } }); setResults(data.results.slice(0, 6)); } catch { setResults([]); }
  };
  const addTruck = (t) => { if (!picked.find((p) => p.slug === t.slug)) setPicked([...picked, { slug: t.slug, name: t.name }]); setQ(""); setResults([]); };
  const removeTruck = (slug) => setPicked(picked.filter((p) => p.slug !== slug));

  const uploadPhoto = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { const { data } = await api.post("/upload", fd); setForm((f) => ({ ...f, photo: data.url })); toast.success("Photo ajoutée"); }
    catch (err) { toast.error(apiError(err)); }
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Donnez un nom à votre marché"); return; }
    setBusy(true);
    const payload = { ...form, truck_slugs: picked.map((p) => p.slug) };
    try {
      if (editing === "new") await api.post("/markets", payload);
      else await api.put(`/markets/${editing}`, payload);
      toast.success("Marché enregistré");
      cancel(); load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const del = async (slug) => {
    if (!window.confirm("Supprimer définitivement ce marché ?")) return;
    try { await api.delete(`/markets/${slug}`); toast.success("Marché supprimé"); load(); } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="bg-white rounded-2xl shadow-warm p-6 mt-6" data-testid="markets-manager">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
        <h2 className="font-display font-bold text-lg text-urban-teal flex items-center gap-2">
          <Calendar size={18} className="text-street-gold" /> Événements & marchés
          <span className="text-[10px] bg-street-gold text-white px-2 py-0.5 rounded-full uppercase tracking-wide">Pro</span>
        </h2>
        {editing === null && <Button onClick={startNew} className="rounded-full bg-street-gold hover:bg-street-gold/90 text-white font-bold gap-1 h-9" data-testid="market-new-btn"><Plus size={15} /> Créer un marché</Button>}
      </div>
      <p className="text-sm text-urban-teal/60 mb-4">Regroupez plusieurs foodtrucks sur un même événement avec une page publique dédiée.</p>

      {editing === null ? (
        markets.length === 0 ? (
          <p className="text-sm text-urban-teal/50" data-testid="markets-empty-manager">Vous n'avez pas encore créé de marché.</p>
        ) : (
          <div className="space-y-2" data-testid="markets-list">
            {markets.map((m) => (
              <div key={m.slug} className="flex items-center justify-between gap-3 border border-cream-bg rounded-xl p-3" data-testid={`market-row-${m.slug}`}>
                <div className="min-w-0">
                  <p className="font-bold text-urban-teal truncate">{m.name}</p>
                  <p className="text-xs text-urban-teal/50 truncate">{m.city || "Lieu non précisé"} · {m.truck_count} foodtruck{m.truck_count > 1 ? "s" : ""}{m.schedule ? ` · ${m.schedule}` : ""}</p>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Link to={`/marche/${m.slug}`} className="text-urban-teal/50 hover:text-street-gold p-2" title="Voir la page" data-testid={`market-view-${m.slug}`}><ExternalLink size={16} /></Link>
                  <button onClick={() => startEdit(m)} className="text-street-gold font-bold text-sm px-2" data-testid={`market-edit-${m.slug}`}>Modifier</button>
                  <button onClick={() => del(m.slug)} className="text-coral-twist p-2" title="Supprimer" data-testid={`market-del-${m.slug}`}><Trash2 size={15} /></button>
                </div>
              </div>
            ))}
          </div>
        )
      ) : (
        <div className="space-y-3" data-testid="market-form">
          <div className="grid sm:grid-cols-2 gap-3">
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Nom du marché" className={INP} data-testid="market-name" />
            <AddressAutocomplete value={form.city} onChange={(v) => setForm({ ...form, city: v })} onSelect={(s) => setForm({ ...form, city: s.city || s.label })} placeholder="Ville" testid="market-city" Icon={MapPin} cityOnly />
          </div>
          <AddressAutocomplete value={form.address} onChange={(v) => setForm({ ...form, address: v })} onSelect={(s) => setForm({ ...form, address: s.label, lat: s.lat, lng: s.lng })} placeholder="Adresse précise (optionnel)" testid="market-address" Icon={MapPin} bias={form.city} />
          <input value={form.schedule} onChange={(e) => setForm({ ...form, schedule: e.target.value })} placeholder="Horaires (ex. Tous les samedis 9h-14h)" className={INP} data-testid="market-schedule" />
          <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} placeholder="Description de l'événement" className={INP} data-testid="market-description" />

          <div>
            <label className="text-xs font-bold uppercase text-urban-teal/50">Photo</label>
            <div className="flex flex-wrap gap-3 mt-2">
              {form.photo && (
                <div className="relative">
                  <img src={mediaUrl(form.photo)} alt="" className="w-20 h-20 object-cover rounded-xl" />
                  <button onClick={() => setForm({ ...form, photo: "" })} className="absolute -top-2 -right-2 bg-coral-twist text-white rounded-full p-1"><Trash2 size={12} /></button>
                </div>
              )}
              {!form.photo && (
                <label className="w-20 h-20 rounded-xl border-2 border-dashed border-urban-teal/30 flex items-center justify-center cursor-pointer text-urban-teal/40 hover:border-street-gold" data-testid="market-photo-upload">
                  <ImageIcon size={22} /><input type="file" accept="image/*" onChange={uploadPhoto} className="hidden" />
                </label>
              )}
            </div>
          </div>

          <div>
            <label className="text-xs font-bold uppercase text-urban-teal/50">Foodtrucks participants ({picked.length})</label>
            {picked.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {picked.map((p) => (
                  <span key={p.slug} className="inline-flex items-center gap-1 bg-cream-bg text-urban-teal text-sm font-semibold rounded-full pl-3 pr-1.5 py-1" data-testid={`market-chip-${p.slug}`}>
                    {p.name}
                    <button onClick={() => removeTruck(p.slug)} className="text-urban-teal/50 hover:text-coral-twist" aria-label="Retirer"><X size={14} /></button>
                  </span>
                ))}
              </div>
            )}
            <div className="relative mt-2">
              <input value={q} onChange={(e) => searchTrucks(e.target.value)} placeholder="Rechercher un foodtruck à ajouter..." className={INP} data-testid="market-truck-search" />
              {results.length > 0 && (
                <div className="absolute z-20 left-0 right-0 bg-white border-2 border-cream-bg rounded-xl mt-1 shadow-warm overflow-hidden" data-testid="market-truck-results">
                  {results.map((t) => (
                    <button key={t.slug} onClick={() => addTruck(t)} className="w-full text-left px-3 py-2 text-sm text-urban-teal hover:bg-cream-bg flex items-center justify-between" data-testid={`market-add-${t.slug}`}>
                      <span>{t.name}</span><span className="text-xs text-urban-teal/40">{t.city}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="flex gap-2 pt-1">
            <Button onClick={save} disabled={busy} className="rounded-full bg-street-gold hover:bg-street-gold/90 text-white font-bold px-6" data-testid="market-save-btn">{busy ? "Enregistrement..." : "Enregistrer"}</Button>
            <Button onClick={cancel} variant="ghost" className="rounded-full text-urban-teal font-semibold" data-testid="market-cancel-btn">Annuler</Button>
          </div>
        </div>
      )}
    </div>
  );
};
